import { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import moment from 'moment-timezone';
import {
  FiCalendar,
  FiUsers,
  FiFileText,
  FiUser,
  FiShield,
  FiClock,
  FiCheckCircle,
  FiXCircle,
} from 'react-icons/fi';

import { textVariantsEnum, fontWeightEnum, Text } from '@/components/Text';
import { Button, buttonVariants } from '@/components/Button';
import { StatusBadge, statusBadgeTones } from '@/components/StatusBadge';
import {
  IconBadge,
  iconBadgeSizes,
  iconBadgeShapes,
} from '@/components/IconBadge';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import {
  LEAVE_STATUS_TONE,
  LEAVE_TYPE_TONE,
  parseLeaveDateString,
} from '@/utils/leaveTaxonomy';
import styles from './Leave.module.scss';

const LEAVE_STATUS_ICON_BG = {
  Pending: 'warning-100',
  Approved: 'success-100',
  Rejected: 'error-100',
  Declined: 'error-100',
};
const LEAVE_STATUS_ICON_COLOR = {
  Pending: 'var(--warning-600)',
  Approved: 'var(--success-600)',
  Rejected: 'var(--error-600)',
  Declined: 'var(--error-600)',
};

function formatDetailDate(date) {
  return date ? moment(date).format('DD/MM/YYYY') : '—';
}

function DetailField({ icon, iconBg, label, value }) {
  return (
    <div className={styles.detailField}>
      <IconBadge icon={icon} bg={iconBg} size={iconBadgeSizes.sm} />
      <div className={styles.detailFieldText}>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Medium}
          color="gray-500"
          className={styles.detailFieldLabel}
        >
          {label}
        </Text>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Semibold}
        >
          {value || '—'}
        </Text>
      </div>
    </div>
  );
}

export const ViewLeaveModal = ({
  isVisible,
  rowData,
  reporterLabel,
  managerLabel,
  departmentLabel,
  onClose,
  title = 'Leave Request Details',
  onApprove,
  onDecline,
  approveLoading = false,
  declineLoading = false,
}) => {
  const [confirmAction, setConfirmAction] = useState(null);

  if (!rowData) return null;

  const isReview = Boolean(onApprove || onDecline);
  const statusIconBg = LEAVE_STATUS_ICON_BG[rowData.status] || 'gray-100';
  const statusIconColor =
    LEAVE_STATUS_ICON_COLOR[rowData.status] || 'var(--gray-600)';

  return (
    <Dialog
      visible={isVisible}
      onHide={onClose}
      closable={!approveLoading && !declineLoading}
      closeOnEscape={!approveLoading && !declineLoading}
      dismissableMask={!approveLoading && !declineLoading}
      style={{ width: '680px' }}
      header={
        <div className={styles.leaveDialogHeader}>
          <IconBadge
            icon={<FiCalendar size={20} color="var(--indigo-600)" />}
            bg="indigo-100"
            size={iconBadgeSizes.lg}
            shape={iconBadgeShapes.circle}
          />
          <div className={styles.leaveDialogHeaderText}>
            <Text
              variant={textVariantsEnum.Text_lg}
              fontWeight={fontWeightEnum.Semibold}
            >
              {title}
            </Text>
          </div>
        </div>
      }
      footer={
        isReview ? (
          <div className={styles.leaveDialogFooter}>
            <Button
              variant={buttonVariants.primary}
              className={styles.reviewPillButton}
              text="Approve"
              onClick={() => setConfirmAction('approve')}
              disabled={approveLoading || declineLoading}
            />
            <Button
              variant={buttonVariants.red}
              text="Reject"
              onClick={() => setConfirmAction('decline')}
              disabled={approveLoading || declineLoading}
            />
          </div>
        ) : (
          <div className={styles.viewDialogFooter}>
            <Button
              variant={buttonVariants.secondary}
              text="Close"
              onClick={onClose}
            />
          </div>
        )
      }
    >
      <div className={styles.leaveFormModalContent}>
        <div className={styles.formRow2Col}>
          <DetailField
            icon={<FiFileText size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Type"
            value={
              <StatusBadge
                label={rowData.type}
                tone={LEAVE_TYPE_TONE[rowData.type] || statusBadgeTones.default}
              />
            }
          />
          <DetailField
            icon={<FiUsers size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Department"
            value={departmentLabel || 'N/A'}
          />
          <div className={styles.gridColumnFull}>
            <DetailField
              icon={<FiFileText size={16} color="var(--indigo-600)" />}
              iconBg="indigo-100"
              label="Comment"
              value={rowData.comment || 'N/A'}
            />
          </div>
          <DetailField
            icon={<FiUser size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Reported By"
            value={reporterLabel || 'N/A'}
          />
          <DetailField
            icon={<FiShield size={16} color={statusIconColor} />}
            iconBg={statusIconBg}
            label="Status"
            value={
              <StatusBadge
                label={rowData.status}
                tone={LEAVE_STATUS_TONE[rowData.status]}
              />
            }
          />
          <DetailField
            icon={<FiClock size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Leave Date"
            value={formatDetailDate(parseLeaveDateString(rowData.leaveDate))}
          />
          <DetailField
            icon={<FiClock size={16} color="var(--indigo-600)" />}
            iconBg="indigo-100"
            label="Report Date"
            value={formatDetailDate(rowData.requestDate?.toDate?.())}
          />
          {managerLabel && (
            <div className={styles.gridColumnFull}>
              <DetailField
                icon={<FiUser size={16} color="var(--indigo-600)" />}
                iconBg="indigo-100"
                label="Manager"
                value={managerLabel}
              />
            </div>
          )}
        </div>
      </div>
      {isReview && (
        <ConfirmActionDialog
          visible={confirmAction === 'approve'}
          onHide={() => setConfirmAction(null)}
          icon={<FiCheckCircle size={18} color="var(--success-600)" />}
          iconBg="success-100"
          title="Approve Leave Request"
          message="Are you sure you want to approve this leave request?"
          confirmLabel="Approve"
          confirmVariant={buttonVariants.primary}
          onConfirm={onApprove}
          loading={approveLoading}
        />
      )}
      {isReview && (
        <ConfirmActionDialog
          visible={confirmAction === 'decline'}
          onHide={() => setConfirmAction(null)}
          icon={<FiXCircle size={18} color="var(--error-600)" />}
          iconBg="error-100"
          title="Reject Leave Request"
          message="Are you sure you want to reject this leave request?"
          confirmLabel="Reject"
          onConfirm={onDecline}
          loading={declineLoading}
        />
      )}
    </Dialog>
  );
};

ViewLeaveModal.displayName = 'ViewLeaveModal';
